import React from 'react';
import { Plus } from '@phosphor-icons/react';
import {
  LiveSearch,
  Spinner,
  Text,
  Flex,
  Button,
  Dialog,
  DialogTrigger,
  DialogFooter,
} from '@chatsdet/ui';
import type { FlowsSearchParams } from '../../../../router';
import { useWorkflowsQuery } from '../../../../hooks/useWorkflows';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import {
  useFlowLineage,
  usePruneDependency,
  useAddDependency,
  type FlowLineage,
} from '../hooks/useFlowLineage';
import { LineageTree } from './LineageTree';

interface FlowLineageCanvasProps {
  workflowId: string;
  /** The focus flow's name — used when labeling new seed links. */
  workflowName: string;
}

/** Pick the single prunable seed link per directly-connected flow. */
function buildPruneMap(
  lineage: FlowLineage,
  focusId: string
): Map<string, { dependentWorkflowId: string; nodeId: string }> {
  const map = new Map<string, { dependentWorkflowId: string; nodeId: string }>();
  for (const edge of lineage.edges) {
    // Direct dependent of the focus: prune the seed node living in that flow.
    if (edge.to === focusId && !map.has(edge.from)) {
      map.set(edge.from, { dependentWorkflowId: edge.from, nodeId: edge.nodeId });
    }
    // Direct dependency of the focus: prune the seed node living in the focus.
    if (edge.from === focusId && !map.has(edge.to)) {
      map.set(edge.to, { dependentWorkflowId: focusId, nodeId: edge.nodeId });
    }
  }
  return map;
}

/**
 * Flow-level dependency lineage rendered on the shared {@link VerticalFlowCanvas}
 * so it is visually identical to the flow designer. Edges are drawn in **run
 * order** (a dependency points to the flows that depend on it), so the canvas's
 * native top→bottom layout reads as execution order: the seed a flow needs sits
 * above it, and the flows that consume this one sit below. The node (×) prunes a
 * direct dependency link; the "Add dependent" picker links an existing flow.
 */
export const FlowLineageCanvas: React.FC<FlowLineageCanvasProps> = ({
  workflowId,
  workflowName,
}) => {
  const lineageQ = useFlowLineage(workflowId);
  const prune = usePruneDependency(workflowId);
  const addDep = useAddDependency(workflowId);
  const [isAdding, setIsAdding] = React.useState(false);
  // Flow id pending a prune-confirm (drives the in-app dialog; null = closed).
  const [pruneCandidate, setPruneCandidate] = React.useState<string | null>(null);

  const lineage = lineageQ.data;

  const flowHref = React.useCallback((id: string) => `/flows/${id}`, []);

  const pruneMap = React.useMemo(
    () => (lineage ? buildPruneMap(lineage, workflowId) : new Map()),
    [lineage, workflowId]
  );
  const prunableIds = React.useMemo(() => new Set(pruneMap.keys()), [pruneMap]);

  const confirmPrune = React.useCallback(() => {
    if (!pruneCandidate) return;
    const target = pruneMap.get(pruneCandidate);
    setPruneCandidate(null);
    if (target) prune.mutate(target);
  }, [pruneCandidate, pruneMap, prune]);

  // Picker candidates: every other flow, minus flows that would create a cycle
  // (anything the focus already depends on, i.e. downstream) and flows that
  // already depend on it directly.
  const excluded = React.useMemo(() => {
    const set = new Set<string>([workflowId]);
    if (lineage) {
      for (const n of lineage.nodes) {
        if (n.direction === 'downstream') set.add(n.id);
      }
      for (const e of lineage.edges) {
        if (e.to === workflowId) set.add(e.from); // already a direct dependent
      }
    }
    return set;
  }, [lineage, workflowId]);

  const [query, setQuery] = React.useState('');
  React.useEffect(() => {
    if (isAdding) setQuery('');
  }, [isAdding]);

  // Search the server so the picker isn't capped at a fixed local page. The
  // query is debounced to avoid a request per keystroke; `useWorkflowsQuery`
  // keys its cache on `search`, so each debounced value refetches.
  const debouncedQuery = useDebouncedValue(query.trim(), 350);

  // Candidate dependents — include `seed` flows (a seed can depend on a seed),
  // which the default flow list hides.
  const candidatesQ = useWorkflowsQuery(
    {
      page: 1,
      pageSize: 50,
      flowType: ['standard', 'seed'],
      search: debouncedQuery || undefined,
    } as FlowsSearchParams,
    isAdding
  );

  // Cycle-exclusion stays client-side: it depends on the lineage graph, not the
  // search text. The server already narrowed the result set by name.
  const candidates = React.useMemo(() => {
    return (candidatesQ.data?.items ?? [])
      .filter((w) => !excluded.has(w.id))
      .map((w) => ({ id: w.id, name: w.name, flowType: w.flowType }));
  }, [candidatesQ.data, excluded]);

  const handleSelectCandidate = React.useCallback(
    (id: string) => {
      addDep.mutate(
        { dependentWorkflowId: id, seedWorkflowName: workflowName },
        { onSuccess: () => setIsAdding(false) }
      );
    },
    [addDep, workflowName]
  );

  if (lineageQ.isLoading || !lineage) {
    return (
      <div className="flex-1 h-full flex items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  const isEmpty = lineage.nodes.length <= 1;

  return (
    <div className="flex-1 h-full relative">
      {isEmpty ? (
        <div className="flex-1 h-full flex items-center justify-center bg-muted/20 rounded-lg">
          <div className="text-sm text-muted-foreground bg-background/80 rounded-md px-4 py-2">
            No dependencies yet — nothing depends on this flow, and it depends on no
            seeds.
          </div>
        </div>
      ) : (
        <LineageTree
          lineage={lineage}
          focusId={workflowId}
          flowHref={flowHref}
          prunableIds={prunableIds}
          onPrune={setPruneCandidate}
          onAddDependent={() => setIsAdding(true)}
        />
      )}

      {/* Prune confirmation — in-app dialog (never a native confirm). */}
      <DialogTrigger
        isOpen={pruneCandidate !== null}
        onOpenChange={(open) => {
          if (!open) setPruneCandidate(null);
        }}
      >
        <span style={{ display: 'none' }} />
        <Dialog title="Remove dependency link?">
          <Text>
            The seed node will be deleted from the dependent flow. This does not delete
            the flow itself.
          </Text>
          <DialogFooter>
            <Flex gap="sm">
              <Button variant="outline" onPress={() => setPruneCandidate(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onPress={confirmPrune}>
                Remove link
              </Button>
            </Flex>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>

      {/* Add-a-dependent palette — the same LiveSearch flow searcher used in suite creation. */}
      <LiveSearch<{ id: string; name: string; flowType: string }>
        isOpen={isAdding}
        onClose={() => setIsAdding(false)}
        query={query}
        onQueryChange={setQuery}
        items={candidates}
        getItemKey={(w) => w.id}
        onSelect={(w) => handleSelectCandidate(w.id)}
        placeholder={`Search a flow to depend on ${workflowName}…`}
        aria-label="Add a flow that depends on this seed"
        hint={
          <Text
            size="xs"
            weight="medium"
            className="px-3 pt-1 uppercase tracking-wider text-muted-foreground"
          >
            Add dependent
          </Text>
        }
        emptyState={
          <div className="py-10 text-center text-sm text-muted-foreground">
            {query.trim()
              ? `No flows match “${query.trim()}”.`
              : 'No eligible flows — everything already depends on this, or would form a cycle.'}
          </div>
        }
        renderItem={(w, { active }) => (
          <Flex align="center" justify="between" gap="sm" className="px-3 py-2.5">
            <Text size="sm" className="truncate">
              {w.name}
            </Text>
            <span
              className={`flex shrink-0 items-center gap-1 text-xs ${
                active ? 'text-foreground' : 'text-muted-foreground/0'
              }`}
            >
              <Plus size={14} /> Add
            </span>
          </Flex>
        )}
        footer={
          <Flex align="center" justify="between" className="px-4 py-2.5">
            <Text size="xs" className="text-muted-foreground">
              The selected flow gets a seed node pointing at {workflowName}.
            </Text>
            <Button variant="secondary" size="sm" onPress={() => setIsAdding(false)}>
              Done
            </Button>
          </Flex>
        }
      />
    </div>
  );
};
